import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { trackAddToCart } from '@/lib/analytics'

/* ─── Tipos exportados ────────────────────────────────────────── */
export interface Producto {
  id: string
  nombre: string
  descripcion: string | null
  precio: number
  precio_oferta: number | null
  stock: number
  orden: number
  categoria: string | null
  imagen_url: string | null
  imagen_detalle_url: string | null
  activo: boolean
  destacado: boolean
  es_nuevo: boolean
  en_calculadora: boolean
  beneficios: string[]
  cobertura: string | null
  slug: string | null
  presentacion: string | null
  volumen_unidad: number | null
}

export interface CartItem {
  producto: Producto
  cantidad: number
  disponible: boolean  // false si el producto fue desactivado o quedó sin stock
}

interface CartContextValue {
  items: CartItem[]
  isOpen: boolean
  origen: 'ecommerce' | 'whatsapp_web'
  totalItems: number
  totalPrice: number
  isEmpty: boolean
  hasUnavailable: boolean
  addItem: (producto: Producto, cantidad?: number) => void
  removeItem: (productoId: string) => void
  updateQty: (productoId: string, cantidad: number) => void
  clearCart: () => void
  openCart: () => void
  closeCart: () => void
  markWhatsappOrigen: () => void
}

/* ─── Persistencia ───────────────────────────────────────────── */
const STORAGE_KEY = 'udgarden_cart_v1'
export const CART_CLEAR_KEY = 'udgarden_cart_clear'

type PersistedCart = {
  items: Array<{ producto: Producto; cantidad: number }>
  origen: 'ecommerce' | 'whatsapp_web'
}

/* ─── Context ────────────────────────────────────────────────── */
const CartContext = createContext<CartContextValue | null>(null)

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems]   = useState<CartItem[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [origen, setOrigen] = useState<'ecommerce' | 'whatsapp_web'>('ecommerce')

  /* ── Hydrate desde localStorage + validar contra la DB ──────── */
  useEffect(() => {
    if (localStorage.getItem(CART_CLEAR_KEY)) {
      localStorage.removeItem(CART_CLEAR_KEY)
      localStorage.removeItem(STORAGE_KEY)
      return
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const persisted = JSON.parse(raw) as PersistedCart
      if (!Array.isArray(persisted.items) || persisted.items.length === 0) return

      // Mostrar inmediatamente desde la caché (optimista)
      setItems(persisted.items.map(i => ({ ...i, disponible: true })))
      if (persisted.origen) setOrigen(persisted.origen)

      // Validar disponibilidad y precios actuales contra Supabase
      const ids = persisted.items.map(i => i.producto.id)
      supabase
        .from('productos')
        .select('id, stock, activo, precio, precio_oferta')
        .in('id', ids)
        .then(({ data }) => {
          if (!data) return
          const map = new Map(data.map(p => [p.id, p]))
          setItems(prev => prev.map(item => {
            const current = map.get(item.producto.id)
            if (!current) return { ...item, disponible: false }
            return {
              ...item,
              disponible: current.activo && current.stock > 0,
              cantidad: Math.min(item.cantidad, current.stock || 0),
              producto: {
                ...item.producto,
                stock: current.stock,
                precio: current.precio,
                precio_oferta: current.precio_oferta,
                activo: current.activo,
              },
            }
          }))
        })
    } catch {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  /* ── Persistir en localStorage ──────────────────────────────── */
  useEffect(() => {
    const persisted: PersistedCart = {
      items: items.map(({ producto, cantidad }) => ({ producto, cantidad })),
      origen,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted))
  }, [items, origen])

  /* ── Acciones ────────────────────────────────────────────────── */
  const addItem = useCallback((producto: Producto, cantidad = 1) => {
    if (producto.stock === 0) return

    setItems(prev => {
      const existing = prev.find(i => i.producto.id === producto.id)
      if (existing) {
        const newQty = Math.min(existing.cantidad + cantidad, producto.stock)
        return prev.map(i =>
          i.producto.id === producto.id
            ? { ...i, cantidad: newQty, disponible: true }
            : i
        )
      }
      return [...prev, { producto, cantidad: Math.min(cantidad, producto.stock), disponible: true }]
    })
    trackAddToCart(producto, cantidad)
    setIsOpen(true)
  }, [])

  const removeItem = useCallback((productoId: string) => {
    setItems(prev => prev.filter(i => i.producto.id !== productoId))
  }, [])

  const updateQty = useCallback((productoId: string, cantidad: number) => {
    setItems(prev => prev.map(i => {
      if (i.producto.id !== productoId) return i
      const capped = Math.max(1, Math.min(cantidad, i.producto.stock))
      return { ...i, cantidad: capped }
    }))
  }, [])

  const clearCart = useCallback(() => {
    setItems([])
    setOrigen('ecommerce')
  }, [])

  const markWhatsappOrigen = useCallback(() => setOrigen('whatsapp_web'), [])

  /* ── Derivados ───────────────────────────────────────────────── */
  const totalItems     = items.reduce((s, i) => s + i.cantidad, 0)
  const totalPrice     = items.reduce((s, i) => s + (i.producto.precio_oferta ?? i.producto.precio) * i.cantidad, 0)
  const isEmpty        = items.length === 0
  const hasUnavailable = items.some(i => !i.disponible)

  return (
    <CartContext.Provider value={{
      items, isOpen, origen,
      totalItems, totalPrice, isEmpty, hasUnavailable,
      addItem, removeItem, updateQty, clearCart,
      openCart:  () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      markWhatsappOrigen,
    }}>
      {children}
    </CartContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used inside CartProvider')
  return ctx
}
